/**
 * In-memory mock backend for the datastore Phase-0 prototype.
 * Simulates the datastore-service REST contract (tables, fields, rows with
 * capped paging, async import/export/convert jobs) including a synthetic
 * 1.2M-row table to exercise the large-data UI states. Phase 1 replaces
 * only datastore.service.ts — this file and the flag go away.
 */
import { reactive } from "vue";
import {
  ColumnMapping,
  ConvertPreview,
  DataStoreAccessLevel,
  DataStoreCell,
  DataStoreField,
  DataStoreFieldType,
  DataStoreJob,
  DataStoreRow,
  DataStoreTable,
  ImportPreview,
  MAP_SKIP,
  RowPage,
  RowQuery,
  ShareTargetPage,
} from "@/datastore/types";
import { validateCell } from "@/datastore/utils/cellValidate";

const LATENCY_MS = 120;
const delay = <T>(v: T): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(v), LATENCY_MS));

let seq = 100;
const nextId = (prefix: string) => `${prefix}-${++seq}`;
const now = () => new Date().toISOString().slice(0, 16).replace("T", " ");

/** Mock privilege store — lets the prototype demonstrate privilege gating. */
export const mockPrivileges = reactive<Record<string, boolean>>({
  "DATA_STORE.PAGE.VIEW": true,
  "DATA_STORE.TABLE.CREATE": true,
  "DATA_STORE.TABLE.EDIT": true,
  "DATA_STORE.TABLE.DELETE": true,
  "DATA_STORE.DATA.VIEW": true,
  "DATA_STORE.DATA.EDIT": true,
  "DATA_STORE.SHARE": true,
  "DATA_STORE.IMPORT": true,
  "DATA_STORE.EXPORT": true,
});

/** The organization the mock's own tables belong to, as the drawer's owner line shows it. */
export const MOCK_OWNER_NAME = "CyanGate";

/**
 * Enough organizations that mock mode demonstrates the case the sharing dialog exists for — a
 * picker that has to be searched and paged rather than scrolled.
 */
export const MOCK_ORGS: { organizationId: string; name: string }[] = [
  { organizationId: "org-acme", name: "Acme Reseller" },
  { organizationId: "org-eu", name: "EU Operations" },
  { organizationId: "org-sandbox", name: "Sandbox Org" },
  ...Array.from({ length: 211 }, (_, i) => ({
    organizationId: `org-gen-${i + 1}`,
    name: `${
      ["Northern", "Southern", "Eastern", "Western", "Central"][i % 5]
    } ${
      ["Partner", "Reseller", "Logistics", "Field Ops", "Data Team"][i % 5]
    } ${i + 1}`,
  })),
];

interface MockTable extends DataStoreTable {
  /** small tables hold rows in memory; big tables synthesize by index */
  rows: DataStoreRow[] | null;
  bigCount?: number;
}

const field = (
  id: string,
  name: string,
  type: DataStoreFieldType,
  ordinal: number,
  over: Partial<DataStoreField> = {}
): DataStoreField => ({ id, name, type, ordinal, ...over });

const countryRows: DataStoreRow[] = (
  [
    ["TR", "Türkiye", 792, false, null],
    ["DE", "Germany", 276, true, "1958-01-01"],
    ["FR", "France", 250, true, "1958-01-01"],
    ["NL", "Netherlands", 528, true, "1958-01-01"],
    ["US", "United States", 840, false, null],
    ["GB", "United Kingdom", 826, false, null],
    ["IT", "Italy", 380, true, "1958-01-01"],
    ["ES", "Spain", 724, true, "1986-01-01"],
    ["PL", "Poland", 616, true, "2004-05-01"],
    ["SE", "Sweden", 752, true, "1995-01-01"],
    ["JP", "Japan", 392, false, null],
    ["BR", "Brazil", 76, false, null],
  ] as [string, string, number, boolean, string | null][]
).map(([code, name, num, eu, joined]) => ({
  id: nextId("r"),
  cells: { f1: code, f2: name, f3: num, f4: eu, f5: joined },
}));

const productRows: DataStoreRow[] = (
  [
    ["SKU-1001", "EXT-88121", "Hardware", 249.9, true],
    ["SKU-1002", "EXT-88122", "Hardware", 119.0, true],
    ["SKU-2001", "EXT-90011", "Software", 49.0, true],
    ["SKU-2002", "EXT-90012", "Software", 99.0, false],
    ["SKU-3001", "EXT-70455", "Service", 1500.0, true],
    ["SKU-3002", "EXT-70456", "Service", 2400.0, true],
  ] as [string, string, string, number, boolean][]
).map(([sku, ext, cat, price, active]) => ({
  id: nextId("r"),
  cells: { f1: sku, f2: ext, f3: cat, f4: price, f5: active },
}));

const tables: MockTable[] = reactive<MockTable[]>([
  {
    id: "t-country",
    name: "Country Codes",
    description: "ISO country lookup used by the SAP flows",
    organizationId: "org-default",
    organizationName: MOCK_OWNER_NAME,
    rowCount: countryRows.length,
    version: 1,
    owned: true,
    access: "MODIFY",
    sharedWith: [
      { organizationId: "org-acme", name: "Acme Reseller", access: "READ" },
      { organizationId: "org-eu", name: "EU Operations", access: "READ_WRITE" },
    ],
    modifiedAt: "2026-07-28 14:12",
    fields: [
      field("f1", "Code", "TEXT", 1, {
        isPrimary: true,
        required: true,
        uniqueKey: true,
        dbIndex: true,
      }),
      field("f2", "Name", "TEXT", 2, { required: true }),
      field("f3", "Numeric Code", "NUMBER", 3),
      field("f4", "EU Member", "BOOLEAN", 4),
      field("f5", "Joined", "DATE", 5),
    ],
    rows: countryRows,
  },
  {
    id: "t-product",
    name: "Product Mapping",
    description: "SKU to external-id mapping for the commerce connector",
    organizationId: "org-default",
    organizationName: MOCK_OWNER_NAME,
    rowCount: productRows.length,
    version: 1,
    owned: true,
    access: "MODIFY",
    sharedWith: [],
    modifiedAt: "2026-07-29 10:41",
    fields: [
      field("f1", "SKU", "TEXT", 1, {
        isPrimary: true,
        required: true,
        uniqueKey: true,
      }),
      field("f2", "External ID", "TEXT", 2, { required: true }),
      field("f3", "Category", "SINGLE_SELECT", 3, {
        settings: { options: ["Hardware", "Software", "Service"] },
      }),
      field("f4", "Price", "NUMBER", 4),
      field("f5", "Active", "BOOLEAN", 5),
    ],
    rows: productRows,
  },
  {
    id: "t-orders",
    name: "Order Lines (imported)",
    description:
      "Imported from orders_2026.csv — exercises capped paging over millions of rows",
    organizationId: "org-default",
    organizationName: MOCK_OWNER_NAME,
    rowCount: 1_248_392,
    version: 1,
    owned: true,
    access: "MODIFY",
    sharedWith: [],
    modifiedAt: "2026-07-30 02:15",
    fields: [
      field("f1", "Order No", "TEXT", 1, { isPrimary: true, dbIndex: true }),
      field("f2", "Line", "NUMBER", 2),
      field("f3", "SKU", "TEXT", 3, { dbIndex: true }),
      field("f4", "Qty", "NUMBER", 4),
      field("f5", "Unit Price", "NUMBER", 5),
      field("f6", "Ship Date", "DATE", 6),
    ],
    rows: null,
    bigCount: 1_248_392,
  },
]);

/** Deterministic row factory for the synthetic big table. */
function bigRow(i: number): DataStoreRow {
  const skus = [
    "SKU-1001",
    "SKU-1002",
    "SKU-2001",
    "SKU-2002",
    "SKU-3001",
    "SKU-3002",
  ];
  const d = new Date(Date.UTC(2026, 0, 1 + (i % 210)));
  return {
    id: `big-${i}`,
    cells: {
      f1: `ORD-${100000 + Math.floor(i / 3)}`,
      f2: (i % 3) + 1,
      f3: skus[i % 6],
      f4: 1 + ((i * 7) % 9),
      f5: ((i * 37) % 40000) / 100 + 5,
      f6: d.toISOString().slice(0, 10),
    },
  };
}

const byId = (id: string): MockTable => {
  const t = tables.find((x) => x.id === id);
  if (!t) throw new Error(`Table not found: ${id}`);
  return t;
};

const touch = (t: MockTable) => {
  t.modifiedAt = now();
  t.version++;
};

/* ---------------- tables ---------------- */

export function mockListTables(search?: string): Promise<DataStoreTable[]> {
  const q = (search ?? "").toLowerCase();
  return delay(
    tables
      .filter((t) => t.name.toLowerCase().includes(q))
      .map((t) => ({ ...t, rows: undefined } as unknown as DataStoreTable))
  );
}

export function mockGetTable(id: string): Promise<DataStoreTable> {
  return delay({ ...byId(id), rows: undefined } as unknown as DataStoreTable);
}

export function mockCreateTable(
  name: string,
  description: string
): Promise<DataStoreTable> {
  if (tables.some((t) => t.name.toLowerCase() === name.toLowerCase())) {
    return Promise.reject(
      new Error("A table with this name already exists in this organization")
    );
  }
  const t: MockTable = {
    id: nextId("t"),
    name,
    description,
    organizationId: "org-default",
    organizationName: MOCK_OWNER_NAME,
    rowCount: 0,
    version: 1,
    owned: true,
    access: "MODIFY",
    sharedWith: [],
    modifiedAt: now(),
    fields: [
      field("f1", "Name", "TEXT", 1, { isPrimary: true, required: true }),
    ],
    rows: [],
  };
  tables.push(t);
  return delay({ ...t, rows: undefined } as unknown as DataStoreTable);
}

export function mockUpdateTable(
  id: string,
  patch: { name?: string; description?: string }
): Promise<void> {
  const t = byId(id);
  if (patch.name) t.name = patch.name;
  if (patch.description !== undefined) t.description = patch.description;
  touch(t);
  return delay(undefined);
}

export function mockDeleteTable(id: string): Promise<void> {
  const i = tables.findIndex((t) => t.id === id);
  if (i >= 0) tables.splice(i, 1);
  return delay(undefined);
}

export function mockShareTargets(
  search: string,
  page: number,
  size: number
): Promise<ShareTargetPage> {
  const term = (search ?? "").trim().toLowerCase();
  const matched = term
    ? MOCK_ORGS.filter((o) => o.name.toLowerCase().includes(term))
    : MOCK_ORGS;
  const from = page * size;
  return delay({
    targets: matched.slice(from, from + size),
    total: matched.length,
    page,
    size,
  });
}

/** Revocations first, then grants — the same order applyShareChanges sends them in. */
export function mockApplyShares(
  id: string,
  grants: { organizationId: string; access: DataStoreAccessLevel }[],
  revoked: string[]
): Promise<void> {
  const t = byId(id);
  t.sharedWith = t.sharedWith.filter(
    (share) => !revoked.includes(share.organizationId)
  );
  grants.forEach((grant) => {
    const name =
      MOCK_ORGS.find((o) => o.organizationId === grant.organizationId)?.name ??
      grant.organizationId;
    const existing = t.sharedWith.find(
      (share) => share.organizationId === grant.organizationId
    );
    if (existing) {
      existing.access = grant.access;
    } else {
      t.sharedWith.push({
        organizationId: grant.organizationId,
        name,
        access: grant.access,
      });
    }
  });
  return delay(undefined);
}

/* ---------------- fields ---------------- */

export function mockAddField(
  tableId: string,
  def: Omit<DataStoreField, "id" | "ordinal">
): Promise<DataStoreField> {
  const t = byId(tableId);
  const ordinal = Math.max(0, ...t.fields.map((f) => f.ordinal)) + 1;
  const f: DataStoreField = { ...def, id: `f${ordinal}-${t.id}`, ordinal };
  t.fields.push(f);
  if (t.rows) {
    t.rows.forEach((r) => {
      r.cells[f.id] = f.type === "BOOLEAN" ? false : null;
    });
  }
  touch(t);
  return delay(f);
}

export function mockUpdateField(
  tableId: string,
  fieldId: string,
  patch: Partial<DataStoreField>
): Promise<void> {
  const t = byId(tableId);
  const f = t.fields.find((x) => x.id === fieldId);
  if (!f) return Promise.reject(new Error("Field not found"));
  Object.assign(f, patch, { id: f.id, ordinal: f.ordinal });
  touch(t);
  return delay(undefined);
}

export function mockDeleteField(
  tableId: string,
  fieldId: string
): Promise<void> {
  const t = byId(tableId);
  const f = t.fields.find((x) => x.id === fieldId);
  if (f?.isPrimary) {
    return Promise.reject(new Error("The primary field cannot be deleted"));
  }
  t.fields = t.fields.filter((x) => x.id !== fieldId);
  if (t.rows) {
    t.rows.forEach((r) => delete r.cells[fieldId]);
  }
  touch(t);
  return delay(undefined);
}

export function mockConvertPreview(
  tableId: string,
  fieldId: string,
  toType: DataStoreFieldType
): Promise<ConvertPreview> {
  const t = byId(tableId);
  const f = t.fields.find((x) => x.id === fieldId);
  if (!f) return Promise.reject(new Error("Field not found"));
  const probe: DataStoreField = { ...f, type: toType, settings: {} };
  let failing = 0;
  const sampleErrors: string[] = [];
  if (t.rows) {
    t.rows.forEach((r, i) => {
      const res = validateCell(r.cells[fieldId], probe);
      if (!res.ok) {
        failing++;
        if (sampleErrors.length < 5) {
          sampleErrors.push(`row ${i + 1}: ${res.error}`);
        }
      }
    });
  } else {
    failing = Math.floor(t.rowCount * 0.00096);
    sampleErrors.push(
      'row 1,204: "4x" is not a valid number',
      'row 18,772: "2026-13-01" is not a valid date'
    );
  }
  return delay({
    total: t.rowCount,
    convertible: t.rowCount - failing,
    failing,
    sampleErrors,
  });
}

/* ---------------- rows ---------------- */

export function mockGetRows(tableId: string, q: RowQuery): Promise<RowPage> {
  const t = byId(tableId);
  const offset = (q.page - 1) * q.pageSize;
  if (t.rows === null) {
    const total = t.bigCount ?? 0;
    const n = Math.max(0, Math.min(q.pageSize, total - offset));
    return delay({
      rows: Array.from({ length: n }, (_, i) => bigRow(offset + i)),
      total,
      offset,
      nextCursor: offset + n < total ? `cursor-${offset + n}` : null,
    });
  }
  const search = (q.search ?? "").toLowerCase();
  const all = search
    ? t.rows.filter((r) =>
        Object.values(r.cells).some((v) =>
          String(v ?? "")
            .toLowerCase()
            .includes(search)
        )
      )
    : t.rows;
  const rows = all.slice(offset, offset + q.pageSize);
  return delay({
    rows: rows.map((r) => ({ id: r.id, cells: { ...r.cells } })),
    total: all.length,
    offset,
    nextCursor: offset + rows.length < all.length ? "cursor-x" : null,
  });
}

export function mockSaveRow(
  tableId: string,
  rowId: string | null,
  cells: Record<string, DataStoreCell>
): Promise<DataStoreRow> {
  const t = byId(tableId);
  if (t.rows === null) {
    return Promise.reject(
      new Error("Rows of the synthetic large table are read-only in the mock")
    );
  }
  const invalid = t.fields
    .map((f) => {
      const res = validateCell(cells[f.id], f);
      if (res.ok) cells[f.id] = res.value ?? null;
      return res;
    })
    .find((res) => !res.ok);
  if (invalid && !invalid.ok) {
    return Promise.reject(new Error(invalid.error));
  }
  if (rowId) {
    const row = t.rows.find((r) => r.id === rowId);
    if (!row) return Promise.reject(new Error("Row not found"));
    row.cells = { ...cells };
    touch(t);
    return delay({ ...row, cells: { ...row.cells } });
  }
  const row: DataStoreRow = { id: nextId("r"), cells: { ...cells } };
  t.rows.push(row);
  t.rowCount++;
  touch(t);
  return delay({ ...row, cells: { ...row.cells } });
}

export function mockDeleteRow(tableId: string, rowId: string): Promise<void> {
  const t = byId(tableId);
  if (t.rows === null) {
    return Promise.reject(
      new Error("Rows of the synthetic large table are read-only in the mock")
    );
  }
  const i = t.rows.findIndex((r) => r.id === rowId);
  if (i >= 0) {
    t.rows.splice(i, 1);
    t.rowCount--;
    touch(t);
  }
  return delay(undefined);
}

/** Answers with the number actually removed, as the batch-delete endpoint does. */
export function mockDeleteRows(
  tableId: string,
  rowIds: string[]
): Promise<number> {
  const t = byId(tableId);
  if (t.rows === null) {
    return Promise.reject(
      new Error("Rows of the synthetic large table are read-only in the mock")
    );
  }
  const doomed = new Set(rowIds);
  const before = t.rows.length;
  t.rows = t.rows.filter((r) => !doomed.has(r.id));
  const deleted = before - t.rows.length;
  if (deleted > 0) {
    t.rowCount -= deleted;
    touch(t);
  }
  return delay(deleted);
}

/* ---------------- jobs (import / export) ---------------- */

const jobs = new Map<string, DataStoreJob>();
const jobTimers = new Map<string, ReturnType<typeof setInterval>>();

export function mockGetJob(jobId: string): Promise<DataStoreJob> {
  const j = jobs.get(jobId);
  if (!j) return Promise.reject(new Error("Job not found"));
  return delay({ ...j, report: [...j.report] });
}

export function mockCancelJob(jobId: string): Promise<void> {
  const j = jobs.get(jobId);
  const timer = jobTimers.get(jobId);
  if (timer) clearInterval(timer);
  if (j && (j.state === "RUNNING" || j.state === "PENDING")) {
    j.state = "CANCELLED";
  }
  return delay(undefined);
}

export function mockStartImport(
  tableId: string,
  file: File,
  header: string[],
  sampleRows: string[][]
): Promise<ImportPreview> {
  const job: DataStoreJob = {
    id: nextId("job"),
    type: "IMPORT",
    tableId,
    state: "PENDING",
    progressPercentage: 0,
    processedRows: 0,
    totalRows: Math.max(2000, Math.round(file.size / 60)),
    report: [],
  };
  jobs.set(job.id, job);
  return delay({
    jobId: job.id,
    fileName: file.name,
    fileSize: file.size,
    header,
    sampleRows,
    estimatedRows: job.totalRows,
  });
}

export function mockRunImportJob(
  jobId: string,
  mapping: ColumnMapping[],
  sampleRows: string[][]
): Promise<void> {
  const job = jobs.get(jobId);
  if (!job) return Promise.reject(new Error("Job not found"));
  const t = byId(job.tableId);
  job.state = "RUNNING";
  const used = mapping.filter((m) => m.target !== MAP_SKIP);
  job.report = [
    `mapped ${used.length} of ${mapping.length} CSV columns`,
    `validated ${Math.min(
      sampleRows.length,
      100
    )} preview rows client-side; server re-validates every row`,
  ];
  const ratePerTick = Math.max(500, Math.floor(job.totalRows / 12));
  const timer = setInterval(() => {
    job.processedRows = Math.min(
      job.totalRows,
      job.processedRows + ratePerTick
    );
    job.progressPercentage = Math.round(
      (job.processedRows / job.totalRows) * 100
    );
    if (job.processedRows >= job.totalRows) {
      clearInterval(timer);
      jobTimers.delete(jobId);
      job.state = "FINISHED";
      job.report.push(`${job.totalRows.toLocaleString()} rows imported`);
      t.rowCount += job.totalRows;
      if (t.rows !== null && t.rowCount > 100_000) {
        // large import flips the mock table into synthetic big mode
        t.rows = null;
        t.bigCount = t.rowCount;
      } else if (t.rows === null) {
        t.bigCount = t.rowCount;
      }
      touch(t);
    }
  }, 600);
  jobTimers.set(jobId, timer);
  return delay(undefined);
}

export function mockStartExport(tableId: string): Promise<DataStoreJob> {
  const t = byId(tableId);
  const job: DataStoreJob = {
    id: nextId("job"),
    type: "EXPORT",
    tableId,
    state: "RUNNING",
    progressPercentage: 0,
    processedRows: 0,
    totalRows: t.rowCount,
    report: [],
  };
  jobs.set(job.id, job);
  const timer = setInterval(() => {
    job.progressPercentage = Math.min(100, job.progressPercentage + 22);
    job.processedRows = Math.floor((job.progressPercentage / 100) * t.rowCount);
    if (job.progressPercentage >= 100) {
      clearInterval(timer);
      jobTimers.delete(job.id);
      job.state = "FINISHED";
    }
  }, 400);
  jobTimers.set(job.id, timer);
  return delay({ ...job, report: [] });
}

export function mockDownloadExport(jobId: string): Promise<Blob> {
  const j = jobs.get(jobId);
  if (!j || j.state !== "FINISHED") {
    return Promise.reject(new Error("Export is not finished"));
  }
  const t = byId(j.tableId);
  const fields = [...t.fields].sort((a, b) => a.ordinal - b.ordinal);
  const source: DataStoreRow[] =
    t.rows ?? Array.from({ length: 50 }, (_, i) => bigRow(i));
  const csv = [
    fields.map((f) => JSON.stringify(f.name)).join(","),
    ...source.map((r) =>
      fields.map((f) => JSON.stringify(r.cells[f.id] ?? "")).join(",")
    ),
  ].join("\n");
  return delay(new Blob([csv], { type: "text/csv" }));
}
